import React, { useState, useMemo } from 'react';
import { Plus, Search, Filter, MoreHorizontal, CreditCard, TrendingUp, Wallet, DollarSign } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { PaymentAccountTable } from './PaymentAccountTable';
import { PaymentAccountForm } from './PaymentAccountForm';
import { PaymentAccountFilters } from './PaymentAccountFilters';
import { usePaymentAccounts } from '../../hooks/usePaymentAccounts';
import { usePaymentAccountMutations } from '../../hooks/usePaymentAccountMutations';
import {
  PaymentAccount,
  PaymentAccountFilters as PaymentAccountFilterType,
  PaymentAccountType,
  PaymentAccountStatus,
  buildPaymentAccountSummary,
  formatCurrency
} from '../../types/payment-account';

export function PaymentAccountPage() {
  const { accounts, loading, error, refetch, filters, setFilters } = usePaymentAccounts();
  const { createAccount, updateAccount, deleteAccount, loading: mutationLoading } = usePaymentAccountMutations();

  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [editingAccount, setEditingAccount] = useState<PaymentAccount | null>(null);

  // Update filters to include search term
  const updatedFilters = useMemo(() => ({
    ...filters,
    search: searchTerm || undefined
  }), [filters, searchTerm]);

  const summary = useMemo(() => buildPaymentAccountSummary(accounts), [accounts]);

  // The usePaymentAccounts hook already handles filtering and sorting
  const filteredAccounts = useMemo(() => {
    if (searchTerm || filters.type || filters.status || filters.bank_name) {
      // Apply local filters for immediate feedback while hooks refetch
      let filtered = [...accounts];

      if (searchTerm) {
        filtered = filtered.filter(account =>
          account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          account.bank_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          account.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (filters.type) {
        filtered = filtered.filter(account => account.type === filters.type);
      }

      if (filters.status) {
        filtered = filtered.filter(account => account.status === filters.status);
      }

      if (filters.bank_name) {
        filtered = filtered.filter(account =>
          account.bank_name?.toLowerCase().includes(filters.bank_name!.toLowerCase())
        );
      }

      return filtered;
    }

    return accounts;
  }, [accounts, searchTerm, filters]);

  const handleSelectAccount = (accountId: string, selected: boolean) => {
    const newSelected = new Set(selectedAccounts);
    if (selected) {
      newSelected.add(accountId);
    } else {
      newSelected.delete(accountId);
    }
    setSelectedAccounts(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedAccounts(new Set(filteredAccounts.map(a => a.id)));
    } else {
      setSelectedAccounts(new Set());
    }
  };

  const handleBulkAction = (action: string) => {
    // TODO: Implement bulk action execution
    setSelectedAccounts(new Set());
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingAccount) {
        // Update existing account
        const updated = await updateAccount(editingAccount.id, data);
        if (updated) {
          setShowForm(false);
          setEditingAccount(null);
          refetch();
        }
      } else {
        // Create new account
        const created = await createAccount(data);
        if (created) {
          setShowForm(false);
          refetch();
        }
      }
    } catch (error) {
      console.error('Error saving account:', error);
    }
  };

  const handleEdit = (account: PaymentAccount) => {
    setEditingAccount(account);
    setShowForm(true);
  };

  const handleDelete = async (accountId: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta conta?')) {
      try {
        const success = await deleteAccount(accountId);
        if (success) {
          refetch();
        }
      } catch (error) {
        console.error('Error deleting account:', error);
      }
    }
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'text-green-600';
    if (balance < 0) return 'text-red-600';
    return 'text-foreground';
  };

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando contas...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-destructive/15 border border-destructive/30 text-destructive p-4 rounded-md">
          <p>Erro ao carregar contas: {error}</p>
          <Button onClick={refetch} variant="outline" size="sm" className="mt-2">
            Tentar novamente
          </Button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Header with summary cards */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Contas de Pagamento</h1>
                <p className="text-muted-foreground mt-1">
                  Gerencie suas contas bancárias, cartões e carteiras digitais
                </p>
              </div>
              <Button
                onClick={() => setShowForm(true)}
                className="gap-2"
                disabled={mutationLoading}
              >
                <Plus className="h-4 w-4" />
                Nova Conta
              </Button>
            </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Total de Contas</CardTitle>
              <CreditCard className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-blue-600">{summary.total}</div>
              <p className="text-xs text-muted-foreground">
                {summary.by_status[PaymentAccountStatus.ACTIVE]} ativas
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
              summary.total_balance >= 0 ? 'from-green-500/10 to-green-600/5' : 'from-red-500/10 to-red-600/5'
            }`} />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Saldo Total</CardTitle>
              <DollarSign className={`h-4 w-4 ${getBalanceColor(summary.total_balance)}`} />
            </CardHeader>
            <CardContent className="relative">
              <div className={`text-2xl font-bold ${getBalanceColor(summary.total_balance)}`}>
                {formatCurrency(summary.total_balance)}
              </div>
              <p className="text-xs text-muted-foreground">
                Saldo consolidado
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Maior Saldo</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-purple-600 truncate">
                {summary.highest_balance[0]?.name || 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.highest_balance[0] ? formatCurrency(summary.highest_balance[0].current_balance) : ''}
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Mais Usada</CardTitle>
              <Wallet className="h-4 w-4 text-cyan-600" />
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-cyan-600 truncate">
                {summary.most_used[0]?.name || 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.most_used[0]?.transaction_count || 0} transações
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista de Contas</CardTitle>
              <CardDescription>
                {filteredAccounts.length} de {accounts.length} contas
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {selectedAccounts.size > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                      Ações ({selectedAccounts.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleBulkAction('activate')}>
                      Ativar selecionadas
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkAction('deactivate')}>
                      Desativar selecionadas
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => handleBulkAction('delete')}
                      className="text-destructive"
                    >
                      Excluir selecionadas
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-muted' : ''}
              >
                <Filter className="h-4 w-4" />
                Filtros
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar contas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {searchTerm && (
              <Badge variant="secondary" className="gap-1">
                {filteredAccounts.length} resultado{filteredAccounts.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          {showFilters && (
            <PaymentAccountFilters
              filters={filters}
              onFiltersChange={setFilters}
            />
          )}

          <PaymentAccountTable
            accounts={filteredAccounts}
            selectedAccounts={selectedAccounts}
            onSelectAccount={handleSelectAccount}
            onSelectAll={handleSelectAll}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      {/* Form Dialog */}
          {showForm && (
            <PaymentAccountForm
              account={editingAccount}
              open={showForm}
              onOpenChange={setShowForm}
              onSubmit={handleFormSubmit}
            />
          )}
        </>
      )}
    </div>
  );
}